/**
 * How the caller sounded: three fixed moods, and what each one means here.
 *
 * A DIFFERENT SHAPE FROM `Categories.tsx`, AND THE DIFFERENCE IS THE POINT. That control edits
 * WHICH categories exist. This one edits nothing about membership - "positive, neutral,
 * negative" is an axis, closed in the platform's code - only the DEFINITION, the line the
 * analyser is shown on every call. A definition is a sentence, so each mood is a box.
 *
 * EDITED IN PLACE, AS SETTINGS V2 DRAWS IT, where each card used to open a dialog. Three boxes
 * of 160 characters fit side by side, and the dialog was a click and a Save for one line.
 * The words wait for the page's one bar like everything else.
 *
 * OURS UNTIL SOMEBODY WRITES THEIRS, AND THE BOX SAYS WHICH - BY MARKING THE OVERRIDE, NOT THE
 * DEFAULT. An empty box shows our wording as its placeholder and is stored as "ours"; a box
 * with words in it is theirs and carries a "Yours" tag. Emptying it is the reset.
 *
 * THE DOT IS THE DONUT'S COLOUR, read from `MOOD_FILL` in `charts.tsx`, so this card and the
 * overview cannot drift into two greens.
 */

import { MOOD_FILL } from "../charts";
import { SettingCard, cx } from "../ui";
import { SENTIMENTS, type Sentiment } from "./settings/draft";

export { SENTIMENTS };
export type { Sentiment };

const WORDS: Record<Sentiment, string> = { positive: "Positive", neutral: "Neutral", negative: "Negative" };

/**
 * What each one means when nobody has written their own. MIRRORED FROM THE WORKER, WHICH IS
 * THE AUTHORITY: `SENTIMENT_DEFINITIONS` in `domain/analysis/sentiment.ts` is what reaches the
 * model. If the two ever disagree, the screen is wrong and the prompt is right.
 */
export const DEFINITIONS: Record<Sentiment, string> = {
  positive: "Customer shows enthusiasm, satisfaction, or appreciation",
  neutral: "Balanced tone, factual discussion, neither positive nor negative",
  negative: "Customer is frustrated, unhappy, or expressing concerns",
};

/** The Worker's cap. */
const MAX_DEFINITION_CHARS = 160;

export function Sentiments({
  value,
  onChange,
}: {
  /** Their words by mood; an empty string is ours. */
  value: Record<Sentiment, string>;
  onChange: (next: Record<Sentiment, string>) => void;
}) {
  return (
    <SettingCard className="grid gap-3.5 p-5">
      <div className="grid gap-1">
        <h3 className="text-base font-semibold text-ink">Sentiment</h3>
        <p className="text-xs text-ink-faint">Three fixed moods. Reword what each one means for your callers. Calls already read keep theirs.</p>
      </div>
      <div className="grid grid-cols-3 gap-2.5 max-[720px]:grid-cols-1">
        {SENTIMENTS.map((sentiment) => (
          <label key={sentiment} className="flex flex-col gap-2 rounded-md border border-line-soft bg-ground p-3">
            <span className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2 text-sm font-semibold text-ink">
                <span aria-hidden="true" className={cx("size-2 flex-none rounded-full", MOOD_FILL[sentiment])} />
                {WORDS[sentiment]}
              </span>
              {value[sentiment].trim() && <span className="text-2xs text-ink-faint">Yours</span>}
            </span>
            <textarea
              aria-label={`What ${WORDS[sentiment]} means`}
              rows={3}
              maxLength={MAX_DEFINITION_CHARS}
              value={value[sentiment]}
              placeholder={DEFINITIONS[sentiment]}
              onChange={(event) => onChange({ ...value, [sentiment]: event.target.value })}
              className="resize-none rounded-sm border border-line bg-paper px-2.5 py-2 text-xs text-ink placeholder:text-ink-ghost"
            />
          </label>
        ))}
      </div>
    </SettingCard>
  );
}
